import { useEffect, useRef, useState, useCallback } from "react";

/**
 * Professional canvas view.
 * - Renders background (image or color) + array of positioned elements.
 * - Element types: "text", "image", "shape" (rect/circle/triangle).
 * - Coordinates are percentages (0-100) so JSON scales at any preview size.
 * - Editable mode:
 *     • click element -> select
 *     • drag body    -> move
 *     • drag SE handle -> resize
 *     • drag rotate handle -> rotate (top center)
 *     • Delete key or Backspace when selected -> delete
 * - Read-only mode: pure presentational (used in template grid).
 */
export function CanvasView({
  canvas,
  format,
  className = "",
  editable = false,
  onElementChange,        // (id, patch) => void
  onElementSelect,        // (id | null) => void
  onElementDelete,        // (id) => void
  selectedId = null,
}) {
  const bg = canvas?.bg || { kind: "color", color: "#0B5CFF" };
  const elements = canvas?.elements || [];
  const ratio = format?.ratio || "16/9";

  const wrapRef = useRef(null);

  const clearSelect = useCallback((e) => {
    if (!editable) return;
    if (e.target === e.currentTarget) onElementSelect?.(null);
  }, [editable, onElementSelect]);

  // Keyboard: delete selected
  useEffect(() => {
    if (!editable) return;
    const onKey = (e) => {
      if (!selectedId) return;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        onElementDelete?.(selectedId);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editable, selectedId, onElementDelete]);

  return (
    <div
      ref={wrapRef}
      className={"relative w-full overflow-hidden bg-black select-none " + className}
      style={{ aspectRatio: ratio }}
      onMouseDown={clearSelect}
    >
      {/* Background */}
      {bg.kind === "image" && bg.url && (
        <img
          src={bg.url}
          alt=""
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          crossOrigin="anonymous"
          referrerPolicy="no-referrer"
          onError={(e) => {
            // fallback: try hqdefault if maxres fails (common on older shorts)
            if (bg.url.includes("maxresdefault")) {
              e.currentTarget.src = bg.url.replace("maxresdefault", "hqdefault");
            }
          }}
        />
      )}
      {bg.kind === "color" && (
        <div
          className="absolute inset-0"
          style={{ background: bg.color || "#0B5CFF" }}
        />
      )}
      {bg.kind === "image" && (
        <div className="absolute inset-0 bg-black/10 pointer-events-none" />
      )}

      {elements.map((el) => (
        el.hidden ? null : (
        <ElementView
          key={el.id}
          el={el}
          wrapRef={wrapRef}
          editable={editable && !el.locked}
          selected={selectedId === el.id}
          onChange={(patch) => onElementChange?.(el.id, patch)}
          onSelect={() => onElementSelect?.(el.id)}
        />
        )
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Individual element renderer
// ---------------------------------------------------------------------------
function ElementView({ el, wrapRef, editable, selected, onChange, onSelect }) {
  const startPointer = useRef(null);
  const [editingText, setEditingText] = useState(false);

  // Common style for the wrapping box
  const box = {
    left: `${el.x || 0}%`,
    top: `${el.y || 0}%`,
    width: el.w != null ? `${el.w}%` : "auto",
    height: el.h != null ? `${el.h}%` : "auto",
    transform: `rotate(${el.rotation || 0}deg)`,
    transformOrigin: "top left",
    opacity: el.opacity != null ? el.opacity : 1,
    cursor: editable ? "move" : "default",
    userSelect: "none",
  };

  const getRect = () => wrapRef.current?.getBoundingClientRect();

  const beginDrag = (e) => {
    if (!editable || editingText) return;
    e.stopPropagation();
    onSelect?.();
    const rect = getRect();
    if (!rect) return;
    startPointer.current = {
      mode: "move",
      startX: e.clientX,
      startY: e.clientY,
      elX: el.x || 0,
      elY: el.y || 0,
      rectW: rect.width,
      rectH: rect.height,
    };
    window.addEventListener("mousemove", onDrag);
    window.addEventListener("mouseup", endDrag);
  };

  const beginResize = (e) => {
    if (!editable) return;
    e.stopPropagation();
    e.preventDefault();
    onSelect?.();
    const rect = getRect();
    if (!rect) return;
    startPointer.current = {
      mode: "resize",
      startX: e.clientX,
      startY: e.clientY,
      elW: el.w != null ? el.w : 30,
      elH: el.h != null ? el.h : 30,
      elSize: el.size || 60,
      rectW: rect.width,
      rectH: rect.height,
      isText: el.type === "text",
    };
    window.addEventListener("mousemove", onDrag);
    window.addEventListener("mouseup", endDrag);
  };

  const beginRotate = (e) => {
    if (!editable) return;
    e.stopPropagation();
    e.preventDefault();
    onSelect?.();
    const target = e.currentTarget.parentElement.parentElement;
    const bb = target.getBoundingClientRect();
    const cx = bb.left + bb.width / 2;
    const cy = bb.top + bb.height / 2;
    startPointer.current = {
      mode: "rotate",
      cx,
      cy,
      startAngle: (Math.atan2(e.clientY - cy, e.clientX - cx) * 180) / Math.PI,
      elRot: el.rotation || 0,
    };
    window.addEventListener("mousemove", onDrag);
    window.addEventListener("mouseup", endDrag);
  };

  const onDrag = (mv) => {
    const s = startPointer.current;
    if (!s) return;
    if (s.mode === "move") {
      const dx = ((mv.clientX - s.startX) / s.rectW) * 100;
      const dy = ((mv.clientY - s.startY) / s.rectH) * 100;
      onChange?.({
        x: clamp(s.elX + dx, -20, 110),
        y: clamp(s.elY + dy, -20, 110),
      });
    } else if (s.mode === "resize") {
      const dx = ((mv.clientX - s.startX) / s.rectW) * 100;
      const dy = ((mv.clientY - s.startY) / s.rectH) * 100;
      if (s.isText) {
        // For text, we scale font-size proportionally to horizontal drag
        // and also adjust width so it wraps.
        const nextW = clamp(s.elW + dx, 5, 120);
        const scale = nextW / (s.elW || 1);
        onChange?.({
          w: nextW,
          size: Math.max(10, Math.round((s.elSize || 60) * scale)),
        });
      } else {
        onChange?.({
          w: clamp(s.elW + dx, 3, 120),
          h: clamp(s.elH + dy, 3, 120),
        });
      }
    } else if (s.mode === "rotate") {
      const cur = (Math.atan2(mv.clientY - s.cy, mv.clientX - s.cx) * 180) / Math.PI;
      const rot = (s.elRot + (cur - s.startAngle)) % 360;
      onChange?.({ rotation: rot });
    }
  };

  const endDrag = () => {
    startPointer.current = null;
    window.removeEventListener("mousemove", onDrag);
    window.removeEventListener("mouseup", endDrag);
  };

  // ---- Body renderers ----
  const renderBody = () => {
    if (el.type === "text") {
      const rect = getRect();
      // Support both `size` (legacy) and `font_size` (structured templates)
      const size = el.font_size || el.size || 60;
      const previewFont = rect ? (rect.width * size) / 1280 : size * 0.4;
      const textStyle = {
        fontSize: `${previewFont}px`,
        fontWeight: el.font_weight || el.weight || 900,
        color: el.color || "#FFFFFF",
        background: el.bg || undefined,
        padding: el.bg ? `${previewFont * 0.1}px ${previewFont * 0.25}px` : undefined,
        fontStyle: el.italic ? "italic" : "normal",
        textAlign: el.align || "left",
        letterSpacing: el.letter_spacing != null
          ? (typeof el.letter_spacing === "number" && Math.abs(el.letter_spacing) > 2
              ? `${previewFont * el.letter_spacing / 100}px`
              : `${el.letter_spacing}em`)
          : "-0.02em",
        lineHeight: el.line_height || 1.05,
        fontFamily: el.font_family || "'Bebas Neue','Anton','Impact',system-ui,sans-serif",
        WebkitTextStroke: (el.stroke_color && el.stroke_width) || (el.stroke && el.stroke_width)
          ? `${el.stroke_width}px ${el.stroke_color || el.stroke}`
          : undefined,
        textShadow: el.shadow
          ? `0 ${el.shadow_offset ?? 4}px ${el.shadow_blur ?? 12}px rgba(0,0,0,.55)`
          : undefined,
        display: "inline-block",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        minWidth: 12,
      };
      if (editingText) {
        return (
          <textarea
            autoFocus
            className="bg-transparent outline outline-2 outline-blue-400 resize-none"
            style={{ ...textStyle, minWidth: 40 }}
            value={el.text || ""}
            onBlur={() => setEditingText(false)}
            onChange={(e) => onChange?.({ text: e.target.value })}
            onKeyDown={(e) => { if (e.key === "Escape") setEditingText(false); }}
          />
        );
      }
      return <div style={textStyle} onDoubleClick={() => setEditingText(true)}>{el.text || " "}</div>;
    }
    if (el.type === "image") {
      return (
        <img
          src={el.url}
          alt=""
          className="w-full h-full pointer-events-none"
          crossOrigin="anonymous"
          referrerPolicy="no-referrer"
          draggable={false}
          style={{ objectFit: el.fit || "cover", borderRadius: el.radius ? `${el.radius}%` : undefined }}
        />
      );
    }
    if (el.type === "shape") {
      // Support gradient / solid / image / none fill for structured templates
      const fillKind = el.fill_kind || "solid";
      let fillCss;
      if (fillKind === "gradient") {
        fillCss = `linear-gradient(${el.gradient_angle || 135}deg, ${el.gradient_from || "#7C3AED"}, ${el.gradient_to || "#0B5CFF"})`;
      } else if (fillKind === "image" && el.fill_image) {
        fillCss = `url("${el.fill_image}") center/cover`;
      } else if (fillKind === "none") {
        fillCss = "transparent";
      } else {
        fillCss = el.fill || "#DC2626";
      }
      // Independent corner radius X/Y (used only for rect)
      const rx = el.radius || 0;
      const ry = el.radius_y != null ? el.radius_y : rx;
      // Effects
      const filters = [];
      if (el.blur) filters.push(`blur(${el.blur}px)`);
      const boxShadows = [];
      if (el.shadow) boxShadows.push(`0 ${el.shadow_offset ?? 4}px ${el.shadow_blur ?? 12}px rgba(0,0,0,.35)`);
      if (el.glow) boxShadows.push(`0 0 ${(el.shadow_blur ?? 12) + 8}px ${el.fill || el.gradient_from || "#7C3AED"}`);
      const common = {
        width: "100%",
        height: "100%",
        background: fillCss,
        border: (el.stroke_width && el.stroke)
          ? `${el.stroke_width}px ${el.dash ? "dashed" : "solid"} ${el.stroke}`
          : ((el.stroke_width && el.stroke_color)
              ? `${el.stroke_width}px ${el.dash ? "dashed" : "solid"} ${el.stroke_color}`
              : undefined),
        boxShadow: boxShadows.length ? boxShadows.join(", ") : undefined,
        filter: filters.length ? filters.join(" ") : undefined,
      };
      if (el.shape === "circle") {
        return <div style={{ ...common, borderRadius: "50%" }} />;
      }
      if (el.shape === "triangle") {
        return (
          <div style={{
            width: 0, height: 0,
            borderLeft: "50% solid transparent",
            borderRight: "50% solid transparent",
            borderBottom: `100% solid ${el.fill || "#DC2626"}`,
            background: "transparent",
          }} />
        );
      }
      return <div style={{ ...common, borderRadius: `${rx}px / ${ry}px` }} />;
    }
    return null;
  };

  return (
    <div
      className={"absolute " + (selected ? "outline outline-2 outline-blue-400" : "")}
      style={box}
      onMouseDown={beginDrag}
      onClick={(e) => { if (editable) { e.stopPropagation(); onSelect?.(); } }}
    >
      {renderBody()}

      {editable && selected && (
        <>
          {/* rotate handle */}
          <div
            onMouseDown={beginRotate}
            className="absolute -top-6 left-1/2 -translate-x-1/2 w-3 h-3 bg-blue-500 rounded-full border-2 border-white shadow cursor-crosshair"
            title="Rotate"
          />
          <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-px h-4 bg-blue-400 pointer-events-none" />
          {/* SE resize handle */}
          <div
            onMouseDown={beginResize}
            className="absolute -bottom-2 -right-2 w-3.5 h-3.5 bg-white border-2 border-blue-500 rounded-sm cursor-se-resize shadow"
            title="Resize"
          />
        </>
      )}
    </div>
  );
}

function clamp(n, min, max) { return Math.max(min, Math.min(max, n)); }
